import { type ReactElement } from "react";

import { type User } from "@internal/user";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { constants } from "@north-beam/nb-common";
import {
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  NBAccountingMode,
} from "@north-beam/nb-common";

import { type Compare } from "../hooks/use-chart-state";

type CompareMetricQuery = {
  queryKey: "metricName";
  queryValue: string;
};

export type CompareDateRangeQuery = {
  queryKey: "compareDateRange";
  queryValue: null;
};

type CompareAttributionModelQuery = {
  queryKey: "attributionModel";
  queryValue: string;
};

type CompareAttributionWindowQuery = {
  queryKey: "attributionWindow";
  queryValue: IMCAttributionWindowDays;
};

type CompareAccountingModeQuery = {
  queryKey: "accountingMode";
  queryValue: INBAccountingMode;
};

export type CompareQuery =
  | CompareMetricQuery
  | CompareDateRangeQuery
  | CompareAttributionModelQuery
  | CompareAttributionWindowQuery
  | CompareAccountingModeQuery;

export type CompareQueryMetadata = {
  label: string;
  icon: ReactElement | null;
  optionLabel: string | FixedDateInterval;
};

export type GetCompareQueryMetadata = (
  compare: Partial<Compare>,
) => CompareQueryMetadata;

export const buildCompareQueryMetadata =
  ({
    attributionMethodEnum,
    metrics,
    compareDateRange,
  }: {
    attributionMethodEnum: User["attributionMethodEnum"];
    metrics: SalesPageMetric[];
    compareDateRange: constants.CompareDateRange | null;
  }): GetCompareQueryMetadata =>
  ({ queryKey, queryValue }: Partial<Compare>) => {
    switch (queryKey) {
      case "metricName": {
        const metric = metrics.find((m) => m.id === queryValue);
        return {
          label: "Metric",
          icon: (
            <i className="fa-regular fa-sharp fa-chart-bar text-inherit w-4" />
          ),
          optionLabel: metric ? metric.name : "",
        };
      }
      case "accountingMode":
        return {
          label: "Accounting mode",
          icon: (
            <i className="fa-regular fa-sharp fa-receipt text-inherit w-4" />
          ),
          optionLabel: queryValue ? NBAccountingMode.getLabel(queryValue) : "",
        };
      case "compareDateRange":
        return {
          label: "Comparison time period",
          icon: <i className="fa-regular fa-sharp fa-clock text-inherit w-4" />,
          optionLabel: compareDateRange
            ? constants.CompareDateRangeTypes.getLabel(compareDateRange.value)
            : "",
        };
      case "attributionModel":
        return {
          label: "Attribution Model",
          icon: (
            <i className="fa-regular fa-sharp fa-chart-network text-inherit -ml-1 w-4" />
          ),
          optionLabel: queryValue
            ? attributionMethodEnum.getLabel(queryValue)
            : "",
        };
      case "attributionWindow":
        return {
          label: "Attribution window",
          icon: (
            <i className="fa-regular fa-sharp fa-calendar-range text-inherit w-4" />
          ),
          optionLabel: `${queryValue} days`,
        };
      default:
        return {
          label: "",
          icon: null,
          optionLabel: "",
        };
    }
  };
